import React, { useState, useEffect } from 'react';

const ColumnSelection = ({ columns, selectedColumns, setSelectedColumns, onDownload, showDetailedDescriptions }) => {
  const [localSelectedColumns, setLocalSelectedColumns] = useState(new Set(selectedColumns));
  const [expandedGroups, setExpandedGroups] = useState({});
  const orderedGroups = ['Player', 'Training', 'Path Integration (PI)', 'Egocentric Pointing', 'Mapping', 'Memory', 'Perspective taking', 'Overall Measures'];

  const detailedGroupDescriptions = {
    // For Detailed Option
    'Player': 'Player information includes ID, age, gender, and education level.',
    'Training': 'This group of variables includes the time it takes to complete each of the phases of the visuospatial training task. Total Training Time is the sum of all training phases.',
    'Path Integration (PI)': 'This group of variables includes error measures and the time taken to complete the PI task. PI Measures include the average distance error (PI Distance), the proportion of under and overestimation relative to the correct distance (PI Distance Ratio<sup>1</sup>), the absolute angle error (PI Absolute Angle), and the total time to complete the PI task (PI Total Time). If no specific PI trials are selected, the averages are calculated using data from all available trials. However, if only certain trials (e.g., PI_trial_0, PI_trial_1, PI_trial_j) are selected, the averages will be computed based on these selected trials only. Users also have the option to extract PI measures for individual trials, including the signed PI angle error (PI Signed Angle).',
    'Egocentric Pointing': 'This group of variables includes the error measures for the Egocentric Pointing Task. Pointing Measures include the average of the absolute pointing error across all trials (Average Absolute Pointing Error) and the average absolute pointing error from each landmark the player pointed from (Average Absolute Pointing Error from Landmark 0). If no specific “pointing from” landmark is selected, the average is computed for all landmarks. If only certain “pointing from” landmarks are selected, the average is computed based on those selected landmarks. Users also have the option to extract the absolute angle error (Absolute Pointing Error from Landmark 0 Trial 0) and the signed angle error (Signed Pointing Error from Landmark 0 Trial 0) for individual trials. If no specific pointing trials are selected, the averages are calculated using data from all available trials. However, if only certain trials are selected, the averages will be computed based on these selected trials only.',
    'Mapping': 'This group of variables includes the degree of fit between the real and estimated map (Mapping R²), the coordinates for the estimated position of the landmarks (Estimated Landmark Coordinates), and the time taken to complete the mapping task (Mapping time).',
    'Memory': 'This group of variables includes the percentage of correct pairings (Memory Percent Correct) and the time taken to complete the task (Memory Total Time).',
    'Perspective taking': 'This group of variables includes the absolute average angular error for all trials in the perspective taking task (Average Absolute Perspective Taking Error) and the time taken to complete the task (Perspective Taking Time). If no specific PI trials are selected, the averages for the angular error are calculated using data from all available trials. However, if only certain trials (e.g., PT_trial 0, PT_trial 1, PT_trial j) are selected, the averages will be computed based on these selected trials only. Users also have the option to extract the absolute angle error (Absolute Perspective Taking Error Trial 0) and the signed angle error (Signed Perspective Taking Error Trial 0) for each trial.',
    'Overall Measures': 'This group of measures includes the start time (SPACE Start Time), end time (SPACE End Time) and total time (SPACE Total Time) to complete the assessment.'
  };

  const quickSummaryGroupDescriptions = {
    // For Summary Option
    'Player': 'Player-related information, such as the player ID.',
    'Training': 'This includes the total time spent during the training session (TotalTrainingTime).',
    'Path Integration (PI)': 'Summary averages of key PI measures, including TotalTime, Distance, and Final Angle. **These averages (Average_PI_TotalTime, Average_PI_Distance, Average_PI_FinalAngle) are calculated by averaging the corresponding measures across all PI trials.**',
    'Egocentric Pointing': 'The overall average of pointing judgment errors across all pointing trials. **The average (Average Absolute Pointing Error) is calculated by averaging the pointing errors across all trials, providing a comprehensive measure of pointing accuracy.**',
    'Mapping': 'The degree of association (R²) between the actual map and the user-created map (MapRSq).',
    'Memory': 'The accuracy percentage of memory tasks (Memory Percent Correct).',
    'Perspective taking': 'The average error in perspective-taking tasks. **The average (Average Absolute Perspective Taking Error) is calculated by averaging the error measures across all perspective-taking trials, providing an overall measure of accuracy in perspective-taking tasks.**',
    'Overall Measures': 'Key timestamps such as SPACE Start Time and SPACE End Time, and the total time of the entire experiment (SPACE Total Time).'
  };

  useEffect(() => {
    setLocalSelectedColumns(new Set(selectedColumns));
  }, [selectedColumns]);

  const handleColumnToggle = (column, parentColumns, isChecked) => {
    setLocalSelectedColumns(prev => {
      const newSet = new Set(prev);

      const toggleColumn = (col) => {
        if (isChecked) {
          newSet.delete(col);
        } else {
          newSet.add(col);
        }
      };

      const toggleGroup = (group) => {
        const childColumns = getChildColumns(group);
        childColumns.forEach(col => {
          if (isChecked) {
            newSet.delete(col);
          } else {
            newSet.add(col);
          }
        });
      };

      if (Array.isArray(parentColumns) || typeof parentColumns === 'string') {
        // Leaf node
        toggleColumn(column);
      } else {
        // Group node
        toggleGroup(parentColumns[column]);
      }
      // After updating the set, immediately update the parent component
      const updatedColumns = Array.from(newSet);
      console.log("Updating selected columns:", updatedColumns);
      setSelectedColumns(updatedColumns);

      return newSet;
    });
  };

  const getChildColumns = (obj) => {
    if (Array.isArray(obj)) {
      return obj;
    }
    if (typeof obj === 'string') {
      return [obj];
    }
    return Object.values(obj).flatMap(getChildColumns);
  };

  const toggleGroup = (group) => {
    setExpandedGroups(prev => ({
      ...prev,
      [group]: !prev[group]
    }));
  };

  const sortColumns = (columns, group) => {
    if (group === 'Path Integration (PI)' || group === 'Perspective taking') {
      return Object.keys(columns).sort((a, b) => {
        if (group === 'Path Integration (PI)') {
          if (a === 'PI Measures') return -1;
          if (b === 'PI Measures') return 1;
        } else if (group === 'Perspective taking') {
          if (a === 'Pespective summaries') return -1;
          if (b === 'Pespective summaries') return 1;
        }
        const aNum = parseInt(a.split('_').pop());
        const bNum = parseInt(b.split('_').pop());
        return aNum - bNum;
      });
    }
    return Object.keys(columns);
  };

  const renderColumns = (columns, depth = 0, group, parent = '') => {
    if (Array.isArray(columns)) {
      return columns.map(column => (
        <div key={column} style={{ marginLeft: `${depth * 20}px`, marginBottom: '8px' }}>
          <label style={{
            display: 'flex',
            alignItems: 'center',
            fontSize: '1rem',
            color: '#4b5563',
            cursor: 'pointer'
          }}>
            <input
              type="checkbox"
              checked={localSelectedColumns.has(column)}
              onChange={() => handleColumnToggle(column, columns, localSelectedColumns.has(column))}
              style={{
                marginRight: '8px',
                width: '18px',
                height: '18px',
                accentColor: '#f97316'
              }}
            />
            {/* Render column with HTML tags for superscript */}
            <span dangerouslySetInnerHTML={{ __html: column.replace(/_/g, ' ') }} />
          </label>
        </div>
      ));
    } else if (typeof columns === 'object') {
      const sortedKeys = sortColumns(columns, group);
      return sortedKeys.map(key => {
        const value = columns[key];
        const childColumns = getChildColumns(value);
        const allSelected = childColumns.every(col => localSelectedColumns.has(col));
        const someSelected = childColumns.some(col => localSelectedColumns.has(col));

        const isExpanded = expandedGroups[`${group}.${key}`];
        const isLeaf = !Array.isArray(value) && typeof value !== 'object';
        const shouldRenderChildren = isExpanded || isLeaf;

        return (
          <div key={key} style={{ marginBottom: '8px', marginLeft: `${depth * 20}px` }}>
            <label style={{
              display: 'flex',
              alignItems: 'center',
              fontSize: depth === 0 ? '1.1rem' : '1rem',
              fontWeight: depth === 0 ? 'bold' : 'normal',
              color: depth === 0 ? '#1f2937' : '#4b5563',
              cursor: 'pointer',
            }}>
              <input
                type="checkbox"
                checked={allSelected}
                ref={input => {
                  if (input) {
                    input.indeterminate = someSelected && !allSelected;
                  }
                }}
                onChange={() => handleColumnToggle(key, columns, allSelected)}
                style={{
                  marginRight: '8px',
                  width: '18px',
                  height: '18px',
                  accentColor: '#f97316'
                }}
              />
              {/* Render key with HTML tags for superscript */}
              <span dangerouslySetInnerHTML={{ __html: key.replace(/_/g, ' ') }} />
              {!isLeaf && (
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    toggleGroup(`${group}.${key}`);
                  }}
                  style={{
                    marginLeft: '8px',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: '0.8rem'
                  }}
                >
                  {isExpanded ? '▼' : '►'}
                </button>
              )}
            </label>
            {shouldRenderChildren && renderColumns(value, depth + 1, group, key)}
          </div>
        );
      });
    }
    return null;
  };

  return (
    <div>
      {orderedGroups.map(group => {
        if (columns[group]) {
          return (
            <div key={group} style={{
              marginBottom: '24px',
              padding: '20px',
              backgroundColor: '#ffffff',
              borderRadius: '8px',
              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1), 0 1px 3px rgba(0, 0, 0, 0.08)', // The trick to make the box pop ；-）
              transition: 'all 0.3s ease',
              transform: 'translateY(0)',
            }}
              onMouseEnter={(e) => {
                e.currentTarget.style.boxShadow = '0 10px 15px rgba(0, 0, 0, 0.1), 0 4px 6px rgba(0, 0, 0, 0.08)';
                e.currentTarget.style.transform = 'translateY(-5px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.boxShadow = '0 4px 6px rgba(0, 0, 0, 0.1), 0 1px 3px rgba(0, 0, 0, 0.08)';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              <h3 style={{
                fontSize: '1.5rem',
                fontWeight: 'bold',
                marginBottom: '16px',
                color: '#1f2937'
              }}>
                {group}
              </h3>
              <p style={{
                marginBottom: '12px',
                color: '#4b5563',
                lineHeight: '1.6', // Increase line height for better readability
                padding: '0 10px' // Add padding for spacing
              }}
                dangerouslySetInnerHTML={{ __html: showDetailedDescriptions ? detailedGroupDescriptions[group] : quickSummaryGroupDescriptions[group] }}
              />
              {renderColumns(columns[group], 0, group)}
              {/* Add footnote for 'Path Integration (PI)' group */}
              {group === 'Path Integration (PI)' && (
                <p style={{ marginBottom: '12px', color: '#4b5563', fontSize: '0.9rem' }}>
                  <sup>1</sup> Zero represents a perfect score while scores below zero represent an underestimation and scores above zero represent an overestimation. Formula: –1 × [1 – (estimated distance / real distance)].
                </p>
              )}
            </div>
          );
        }
        return null;
      })}
      <button
        onClick={() => {
          const finalSelectedColumns = Array.from(localSelectedColumns);
          console.log("Columns being sent for download:", finalSelectedColumns);
          setSelectedColumns(finalSelectedColumns);
          onDownload();
        }}
        style={{
          marginTop: '24px',
          marginBottom: '30px',
          backgroundColor: '#f97316',
          color: 'white',
          padding: '12px 24px',
          borderRadius: '4px',
          border: 'none',
          cursor: 'pointer',
          fontWeight: 'bold',
          fontSize: '1rem',
          transition: 'background-color 0.3s ease'
        }}
        onMouseOver={(e) => e.target.style.backgroundColor = '#fb923c'}
        onMouseOut={(e) => e.target.style.backgroundColor = '#f97316'}
      >
        Download Selected Columns
      </button>
    </div>
  );
};

export default ColumnSelection;